import { Image } from "@cmp-src/data-display/Image/Image";
import { CloseIcon } from "@cmp-src/icons/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@cmp-src/primitives/tooltip";
import { cn } from "@gettactic/helpers";
import { getAvatarGradient } from "@gettactic/helpers/src/colors";
import { getContrastTextColor } from "@gettactic/helpers/src/colors/getContrastTextColor";
import React from "react";

export interface ChipProps {
  className?: string;
  isDisabled?: boolean;
  /** Picture shown before the text. */
  pictureUrl?: string | null;
  /** Shown instead of the picture when there is none, e.g. initials. */
  pictureFallback?: React.ReactNode;
  /** Click on the chip itself (mouse only; give keyboard users another control). */
  onClick?: (event: React.MouseEvent) => void;
  /** Only reserves the actions area; no hide button is rendered. */
  onHide?: () => void;
  /** Shows the remove button, named "Remove" plus the chip's text. */
  onCancel?: (event: React.MouseEvent) => void;
  borderColor?: string;
  /** Background of the fallback, as a gradient. */
  fallbackColor?: string;
  /**
   * Content shown on hover. When set, the tooltip is always available,
   * otherwise it only appears when the label is truncated.
   */
  tooltip?: React.ReactNode;
}

const classes = {
  Wrapper:
    "relative flex items-center gap-[6px] overflow-hidden rounded-[30px] border-1 border-neutral-100 bg-neutral-50 p-1 text-neutral-1000 hover:cursor-pointer hover:border-neutral-200 select-none",
  Image: "h-6 w-6 overflow-hidden rounded-full",
  Text: "overflow-hidden text-nowrap text-ellipsis text-[13px] max-w-[150px]",
  ActionsWrapper:
    "absolute right-0 pr-1 pl-4 top-0 bottom-0 flex items-center justify-center",
  Button: "text-neutral-700 hover:text-neutral-1000",
  ButtonDisabled: "hover:text-neutral-700",
  Icon: "size-4",
  DisabledWrapper: "opacity-60 hover:cursor-default",
  Fallback:
    "flex h-6 w-6 items-center justify-center bg-neutral-300 rounded-full overflow-hidden text-xs text-neutral-700"
};

/**
 * A rounded pill for a person, an email or a tag, with an optional picture (or initials) and a
 * remove button (`onCancel`). Long text is truncated and shown whole in a tooltip. For a user
 * use `UserChip`.
 */
export const Chip: React.FC<React.PropsWithChildren<ChipProps>> = ({
  className,
  isDisabled = false,
  pictureUrl,
  children,
  onClick,
  onHide,
  onCancel,
  pictureFallback,
  borderColor,
  fallbackColor,
  tooltip
}) => {
  const hasActions = !!onCancel || !!onHide;
  const textRef = React.useRef<HTMLDivElement>(null);
  // The remove button is named "Remove" plus the chip's text
  const textId = React.useId();
  const removeId = React.useId();
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(
          textRef.current.scrollWidth > textRef.current.clientWidth
        );
      }
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [children]);

  const chipContent = (
    <div
      className={cn(
        classes.Wrapper,
        isDisabled && classes.DisabledWrapper,
        className
      )}
      onClick={onClick}
      style={borderColor ? { borderColor } : undefined}
    >
      {pictureUrl ? (
        <div>
          <Image src={pictureUrl} className={classes.Image} />
        </div>
      ) : pictureFallback ? (
        <div
          className={classes.Fallback}
          style={
            fallbackColor
              ? {
                  backgroundImage: getAvatarGradient(fallbackColor),
                  color: getContrastTextColor(fallbackColor)
                }
              : undefined
          }
        >
          {pictureFallback}
        </div>
      ) : undefined}
      <div
        ref={textRef}
        id={textId}
        className={cn(
          classes.Text,
          !pictureUrl && !pictureFallback ? "ml-1" : ""
        )}
      >
        {children}
      </div>
      {hasActions && (
        <>
          <div className={"w-4"} />

          <div
            className={classes.ActionsWrapper}
            style={{
              // TODO: Make it a tailwind variable
              background:
                "linear-gradient(270deg, #F0F4F7 66.43%, rgba(240, 244, 247, 0) 100%)"
            }}
          >
            {onCancel ? (
              <button
                className={cn(
                  classes.Button,
                  isDisabled && classes.ButtonDisabled
                )}
                disabled={isDisabled}
                type={"button"}
                id={removeId}
                aria-labelledby={`${removeId} ${textId}`}
                onClick={onCancel}
              >
                <span className="sr-only">Remove</span>
                <CloseIcon className={classes.Icon} />
              </button>
            ) : undefined}
          </div>
        </>
      )}
    </div>
  );

  if (!tooltip && !isTruncated) {
    return chipContent;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{chipContent}</TooltipTrigger>
        <TooltipContent>{tooltip ?? children}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
